import {type FC, useCallback, useEffect, useRef} from 'react';
import LeftMenu from '../layouts/menu/LeftMenu.tsx';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router';
import StreamingPage from './stream/StreamingPage.tsx';
import ReplayPage from './replay/ReplayPage.tsx';
import MessagePage from './message/MessagePage.tsx';
import VideoCallPage from './video_call/VideoCallPage.tsx';
import BusInfoPage from './bus_info/BusInfoPage.tsx';
import IpTvPage from './ipTV/IpTvPage.tsx';
import styled from 'styled-components';
import useCenterStore from '../store/useCenterStore.ts';
import * as React from 'react';
import MainLayout from '../layouts/MainLayout.tsx';
import useStreamingStore from '../store/useStreamingStore.ts';
import {useQuery} from '@tanstack/react-query';
import callApi from '../apis/callApi.ts';
import useVideoCallStore from '../store/useVideoCallStore.ts';
import useModal from '../hooks/useModal.tsx';
import ModalRenderer from '../compoents/modal/ModalRenderer.tsx';
import type {PopupType} from '../compoents/modal';
import useGlobalStore from '../store/useGlobalStore.ts';
import {setFocus} from '@noriginmedia/norigin-spatial-navigation';
import DeviceIDError from './error/DeviceIDError.tsx';
import menuData from '../layouts/menu/types/menuData.json';
import useMessageStore from '../store/useMessageStore.ts';
import AmenitiesPage from './amenities/AmenitiesPage.tsx';
import FoodInfoPage from './food_info/FoodInfoPage.tsx';
import FoodInfoDetailPage from './food_info/component/FoodInfoDetailPage.tsx';
import BrowserStorageUtil from '../utils/BrowserStorageUtil.ts';
import {stripQuotes} from '../utils/StringUtil.ts';
import {readRustDeskId} from '../apis/rustdeskId';
import Watching from './over_watch/Watching.tsx';
import BroadcastPage from './broadcast/BroadcastPage.tsx';
import type {CenterInfo} from '../models';
import Package from '../../package.json';

const MainContent = styled.div`
    grid-area: 2 / 1 / 12 / 9;
    width: 100%;
    height: 100%;
    //height: 780px;
    min-height: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px;
    background-color: #1c1c1c;
    border-radius: 1.5rem;
    overflow: hidden;
`;

const isPopupType = (value: any): value is PopupType => {
    return ['message', 'notice', 'video_call'].includes(value);
};

const FEATURE_MAP: Record<string, string> = {
    streaming: 'live_streaming',
    iptv: 'iptv',
    replay: 'vod',
    message: 'message',
    videoCall: 'video_call',
    // 편의 시설로 바뀌어야함.
    busArrived: 'bus',
};

const Main: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const accessToken = stripQuotes(token);
    const refreshToken = searchParams.get('refreshToken');
    if (accessToken) {
        BrowserStorageUtil.setItem('ACCESSTOKEN_KEY', accessToken);
    }
    if (refreshToken) {
        BrowserStorageUtil.setItem('REFRESHTOKEN_KEY', refreshToken);
    }

    const searchParamDeviceId = searchParams.get('deviceID');
    const {
        setVideoMode,
        centerInfo,
        deviceID,
        setDeviceID,
        getCenterInfo,
        setCenterInfo,
        electronVer,
        setElectronVer
    } = useCenterStore();

    const prevTimeRef = useRef<number>(Date.now());
    const prevMenuRef = useRef<string | null>(null);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const ONE_HOUR = 60 * 60 * 1000;

    const fetchStatistics = useCallback(
        async (feature: string) => {
            const now = Date.now();
            const duration = Math.floor((now - prevTimeRef.current) / 1000);
            prevTimeRef.current = now;
            if (!centerInfo?.center_id) {
                return;
            }
            // 원격지원 ID 는 기기(셋톱박스) 속성이라 device_id 와 짝으로 보내야 서버가
            // 어느 기기 행에 기록할지 정할 수 있다. 둘 중 하나만 가면 서버는 무시한다.
            const rustdeskId = await readRustDeskId();
            const params: Record<string, unknown> = {
                center_code: centerInfo?.center_id,
                feature_code: feature,
                duration,
                grade: 'info',
                tv_client_ver: electronVer,
                web_client_ver: Package.version,
                device_id: deviceID,
                rustdesk_id: rustdeskId
            };
            const filteredEntries = Object.entries(params).filter(([_, v]) => {
                if (v === null || v === undefined) return false;
                if (typeof v === 'string' && v.trim() === '') return false;
                return true;
            });
            const qs = new URLSearchParams(
                filteredEntries.map(([k, v]) => [k, String(v)])
            ).toString();
            const url = `statistic/client/?${qs}`;
            await callApi().get(url);
        },
        [centerInfo?.center_id, electronVer, deviceID]
    );

    useEffect(() => {
        if (!deviceID) return;
        const startInterval = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            intervalRef.current = setInterval(() => {
                const currentMenu = location.pathname.replace('/', '');
                const feature = FEATURE_MAP[currentMenu];
                if (feature) {
                    fetchStatistics(feature);
                }
            }, ONE_HOUR);
        };
        startInterval();
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [deviceID, location.pathname, fetchStatistics]);

    useEffect(() => {
        const currentMenu = location.pathname.replace('/', '');

        if (deviceID && prevMenuRef.current) {
            const feature = FEATURE_MAP[prevMenuRef.current];
            if (feature) fetchStatistics(feature);
        }

        prevMenuRef.current = currentMenu;
    }, [location.pathname, deviceID, fetchStatistics]);

    const fetchData = async () => {
        if (window.electronAPI) {
            const data = await window.electronAPI.getAppInfo();
            setElectronVer(data.version);
        } else {
            setElectronVer("");
        }
    };

    useEffect(() => {
        if (token) {
            try {
                const payload = token.split('.')[1];
                const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
                const decoded = JSON.parse(atob(base64));
                console.log('Decoded token:', decoded);

                localStorage.setItem('ACCESS_TOKEN', token);

                const centerData: CenterInfo = {
                    center_id: `ICS-${String(decoded.center_data.center_number).padStart(3, '0')}`,
                    id: decoded.center_data.id,
                    name: decoded.center_data.name,
                    address: decoded.center_data.address || undefined,
                    latitude: decoded.center_data.latitude,
                    longitude: decoded.center_data.longitude,
                    district: undefined,
                    health_care_device_id: undefined,
                    manager: null,
                    smart_table_id: undefined,
                    walking_machine_id: undefined,
                };

                setCenterInfo(centerData);
                setDeviceID(decoded.center_data.device_id);
            } catch (e) {
                console.error('Token 디코드 실패:', e);
            }
        }
    }, [token]);

    useEffect(() => {
        fetchData();
        if (isBroadcast || isWatching) {
            return;
        } else {
            if (!isWatching) {
                if (token) {
                    // token useEffect에서 처리
                    return;
                }
                if (searchParamDeviceId) {
                    setDeviceID(searchParamDeviceId);
                } else {
                    navigate('/error');
                }
            }
        }
    }, []);

    const {showPopup} = useModal();
    const {generateId} = useGlobalStore();
    const {streamingPollingData, setStreamingPollingData} = useStreamingStore();
    const {setIsCalling, setCurrentPage, setRoomName, setTokenByRoomName} = useVideoCallStore();
    const {setCurrentPage: messageSetCurrentPage, setSelectedMessage} = useMessageStore();

    const setReceived = async (data: any, centerName: string) => {
        setRoomName(data.video_call.title);
        await setTokenByRoomName(centerName);
        if (data.video_call) {
            setCurrentPage('detail');
            setIsCalling(true);
        }
    };

    useQuery({
        queryKey: ['streamingPollingData', centerInfo?.center_id],
        queryFn: async () => {
            if (!centerInfo?.center_id) return null;
            // const res = await callApi().get("https://media.smartsilver.life/status");
            // setIsHlsServerOn(JSON.parse(res.data.status));
            const path = location.pathname.split('/').filter(Boolean)[0] || '';
            const res = await callApi().get(
                `/center/polling/?center_code=${centerInfo.center_id}&current_menu=${path}`
            );
            const {data} = res;
            setStreamingPollingData(data);
            for (const props in data) {
                if (isPopupType(props) && data[props]) {
                    if (props === 'notice') {
                        // TEMP
                        if (localStorage.getItem('notice') === data[props].id + '') continue;
                        if (
                            streamingPollingData === null ||
                            (streamingPollingData && streamingPollingData[props].id !== data[props].id)
                        ) {
                            const id = generateId();
                            showPopup('notice', {
                                id: id,
                                title: '공지사항',
                                content: data[props].content,
                            });
                            // TEMP
                            localStorage.setItem('notice', data[props].id);
                        }
                    } else if (props === 'video_call') {
                        // TEMP
                        if (localStorage.getItem('video_call') === data[props].id + '') continue;
                        if (
                            streamingPollingData === null ||
                            (streamingPollingData && streamingPollingData[props].id !== data[props].id)
                        ) {
                            const id = generateId();
                            showPopup(
                                'video_call',
                                {
                                    id: id,
                                    title: data[props].title,
                                    center_name: data[props].center_name,
                                },
                                {
                                    onConfirm: () => {
                                        navigate(`/videoCall?deviceID=${deviceID}`);
                                        setReceived(data, centerInfo.name);
                                        setTimeout(() => {
                                            setFocus('video-call-mute');
                                        }, 2000);
                                    },
                                }
                            );
                            // TEMP
                            localStorage.setItem('video_call', data[props].id);
                        }
                    } else if (props === 'message') {
                        // TEMP
                        if (localStorage.getItem('message') === data[props].id + '') continue;
                        if (
                            streamingPollingData === null ||
                            (streamingPollingData && streamingPollingData[props].id !== data[props].id)
                        ) {
                            const id = generateId();
                            showPopup(
                                'message',
                                {
                                    id: id,
                                    title: data[props].title,
                                    center_name: data[props].author,
                                    content: data[props].content,
                                },
                                {
                                    onConfirm: () => {
                                        navigate(`/message?deviceID=${deviceID}`);
                                        messageSetCurrentPage('detail');
                                        setSelectedMessage(data['message']);
                                        setTimeout(() => {
                                            setFocus('back-button');
                                        }, 1000);
                                    },
                                }
                            );
                            // TEMP
                            localStorage.setItem('message', data[props].id);
                        }
                    } else {
                        continue;
                    }
                }
            }
            return data;
        },
        enabled: !!centerInfo?.center_id,
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 30,
        refetchIntervalInBackground: false,
        retry: 1,
        retryDelay: 1000,
    });

    const validPaths = menuData.flatMap((item) => {
        const paths = [`/${item.name}`];
        if (item.child) {
            paths.push(...item.child.map((c) => `/${c.name}`));
        }
        return paths;
    });
    validPaths.push('/');
    const isValidPath = validPaths.some(
        (path) => location.pathname === path || location.pathname.startsWith(path + '/')
    );
    const isBroadcast = location.pathname === '/broadcast';
    const isWatching = location.pathname === '/watching';
    const getPageComponent = (name: string) => {
        switch (name) {
            case 'streaming':
                return <StreamingPage/>;
            case 'iptv':
                return <IpTvPage/>;
            case 'replay':
                return <ReplayPage/>;
            case 'message':
                return <MessagePage/>;
            case 'videoCall':
                return <VideoCallPage/>;
            case 'amenities':
                return <AmenitiesPage/>;
            case 'busArrived':
                return <BusInfoPage/>;
            case 'foodInfo':
                return <FoodInfoPage/>;
            case 'watching':
                return <Watching/>;
            default:
                return <DeviceIDError/>;
        }
    };

    const setCpuInfo = async () => {
        if (!window.electronAPI && !centerInfo) {
            await getCenterInfo(deviceID);
        }
        if (window.electronAPI) {
            const {cpuInfo} = await window.electronAPI.getCpuInfo();
            localStorage.setItem('cpu-info', cpuInfo);
            setVideoMode('high-quality');
            if (cpuInfo.includes("100")) {
                setVideoMode('best-perform');
            } else {
                setVideoMode('high-quality');
            }
        } else {
            localStorage.setItem('cpu-info', '');
            setVideoMode('high-quality');
        }
    };

    useEffect(() => {
        setCpuInfo();
    }, []);

    return (
        <>
            {isValidPath ? (
                <MainLayout>
                    <LeftMenu/>
                    <MainContent>
                        <Routes>
                            <Route path="/" element={<Navigate to={`/streaming`} replace/>}/>
                            {menuData.map((item) => (
                                <Route key={item.id} path={`/${item.name}`} element={getPageComponent(item.name)}/>
                            ))}
                            <Route path="/busArrived" element={<BusInfoPage/>}/>
                            <Route path="/foodInfo" element={<FoodInfoPage/>}/>
                            <Route path="/watching" element={<Watching/>}/>
                            <Route path="/foodInfo/:id" element={<FoodInfoDetailPage/>}/>
                        </Routes>
                    </MainContent>
                    <ModalRenderer/>
                </MainLayout>
            ) : isBroadcast ? (
                <BroadcastPage/>
            ) : isWatching ? (
                <Watching/>
            ) : (
                <DeviceIDError/>
            )}
        </>
    );
};

export default Main;
